import { useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { ImageIcon, Loader2, Sparkles, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

// Batch-size presets. The backend caps at 25, keeping each click bounded
// so a user can iterate without worrying about runaway spend / timeouts.
const BATCH_SIZES = [5, 10, 20];

export default function BatchFillImagesDialog() {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [brandFilter, setBrandFilter] = useState("__all__");
  const [batchSize, setBatchSize] = useState(10);
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const loadPreview = async (next = {}) => {
    setBusy(true);
    try {
      const payload = { dry_run: true, limit: next.batchSize ?? batchSize };
      const brand = next.brandFilter ?? brandFilter;
      if (brand && brand !== "__all__") payload.brand = brand;
      const { data } = await api.post("/admin/fill-missing-images", payload);
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load preview");
    } finally {
      setBusy(false);
    }
  };

  const onOpenChange = (v) => {
    setOpen(v);
    if (v) {
      setLastResult(null);
      loadPreview();
    }
  };

  const runBatch = async () => {
    if (!preview || preview.will_process === 0) return;
    setBusy(true);
    try {
      const payload = { dry_run: false, limit: batchSize };
      if (brandFilter && brandFilter !== "__all__") payload.brand = brandFilter;
      // GPT image-1 ~5-10s per image → give plenty of headroom per batch.
      const { data } = await api.post("/admin/fill-missing-images", payload, { timeout: 10 * 60 * 1000 });
      setLastResult(data);
      await loadPreview();  // refresh counts
      if (data.failed_count === 0) {
        toast.success(`Generated ${data.processed_count} images`);
      } else {
        toast.warning(`Generated ${data.processed_count}, ${data.failed_count} failed`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button
          data-testid="open-fill-images-btn"
          variant="outline"
          size="sm"
          className="bg-zinc-900 border-zinc-700 text-zinc-300 hover:text-zinc-100 hover:border-fuchsia-500/40"
        >
          <ImageIcon className="w-3.5 h-3.5 mr-1.5" /> Fill Missing Images
        </Button>
      </DialogTrigger>
      <DialogContent
        data-testid="fill-images-dialog"
        className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-zinc-100 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-fuchsia-300" /> AI Fill Missing Catalog Images
          </DialogTitle>
          <p className="text-xs text-zinc-500">
            GPT image-1 generates a clean studio shot per release using brand + colorway + vehicle.
            Costs Emergent credits — ~$0.04 / image. Run in batches.
          </p>
        </DialogHeader>

        {!preview ? (
          <div className="py-10 flex items-center justify-center text-zinc-500">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : (
          <div className="space-y-4 mt-2">
            {/* Headline totals */}
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Total missing" value={preview.total_missing} accent="text-rose-300" />
              <Stat label="Will process" value={preview.will_process} accent="text-fuchsia-300" />
              <Stat
                label="Batches remaining"
                value={preview.will_process > 0 ? Math.ceil(preview.total_missing / batchSize) : 0}
                accent="text-zinc-200"
              />
            </div>

            {/* Brand filter + batch size */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-300 text-[11px] uppercase tracking-wider">Brand filter</Label>
                <Select
                  value={brandFilter}
                  onValueChange={(v) => {
                    setBrandFilter(v);
                    loadPreview({ brandFilter: v });
                  }}
                >
                  <SelectTrigger data-testid="fill-images-brand-select" className="bg-zinc-900 border-zinc-700 text-zinc-100 mt-1">
                    <SelectValue placeholder="All brands" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="__all__">All brands</SelectItem>
                    {(preview.by_brand || []).map((b) => (
                      <SelectItem key={b.brand} value={b.brand}>
                        {b.brand} <span className="opacity-60 ml-2">{b.missing}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-300 text-[11px] uppercase tracking-wider">Batch size</Label>
                <div className="flex gap-1.5 mt-1">
                  {BATCH_SIZES.map((n) => (
                    <button
                      key={n}
                      type="button"
                      data-testid={`fill-images-batch-${n}`}
                      onClick={() => {
                        setBatchSize(n);
                        loadPreview({ batchSize: n });
                      }}
                      className={`flex-1 px-3 py-2 rounded-md text-sm font-semibold border transition ${
                        batchSize === n
                          ? "bg-fuchsia-500/20 border-fuchsia-400/60 text-fuchsia-100"
                          : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-zinc-200"
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Per-brand breakdown */}
            {preview.by_brand?.length > 0 && brandFilter === "__all__" && (
              <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3" data-testid="fill-images-brand-breakdown">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Missing by brand</p>
                <ul className="text-xs space-y-1.5">
                  {preview.by_brand.map((b) => (
                    <li key={b.brand} className="flex items-center justify-between">
                      <span className="text-zinc-200">{b.brand}</span>
                      <span className="font-mono text-rose-300">{b.missing}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Last batch result */}
            {lastResult && (
              <div
                data-testid="fill-images-last-result"
                className={`rounded-lg border p-3 ${
                  lastResult.failed_count > 0
                    ? "border-amber-500/40 bg-amber-500/5"
                    : "border-emerald-500/40 bg-emerald-500/5"
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  {lastResult.failed_count > 0
                    ? <AlertTriangle className="w-4 h-4 text-amber-300" />
                    : <CheckCircle2 className="w-4 h-4 text-emerald-300" />}
                  <span className="text-sm font-semibold text-zinc-100">
                    {lastResult.processed_count} generated
                    {lastResult.failed_count > 0 ? ` · ${lastResult.failed_count} failed` : ""}
                  </span>
                  <span className="text-[10px] text-zinc-500 ml-auto font-mono">{lastResult.remaining} remaining</span>
                </div>
                {lastResult.processed?.length > 0 && (
                  <ul className="text-[11px] text-zinc-400 space-y-0.5 max-h-24 overflow-y-auto">
                    {lastResult.processed.slice(0, 8).map((p) => (
                      <li key={p.release_id} className="truncate">✓ {p.release_name}</li>
                    ))}
                  </ul>
                )}
                {lastResult.failed?.length > 0 && (
                  <ul className="text-[11px] text-amber-400 space-y-0.5 mt-2 max-h-24 overflow-y-auto">
                    {lastResult.failed.slice(0, 5).map((p) => (
                      <li key={p.release_id} className="truncate">✕ {p.release_name} — {p.error}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <Button
              data-testid="fill-images-run-btn"
              onClick={runBatch}
              disabled={busy || !preview.will_process}
              className="w-full bg-fuchsia-600 hover:bg-fuchsia-500 text-white font-semibold"
            >
              {busy ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating… this can take a minute</>
              ) : preview.will_process === 0 ? (
                "All done — no releases need images"
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" /> Generate {preview.will_process} images</>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, accent = "text-zinc-100" }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 px-3 py-2.5">
      <p className={`text-2xl font-mono font-bold ${accent}`}>{(value || 0).toLocaleString()}</p>
      <p className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</p>
    </div>
  );
}
